import React from "react";
import { Box, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PaidIcon from "@mui/icons-material/Paid";
import { toast } from "react-toastify";
import { COLORS } from "../../assets/theme";
import CustomTable from "../../components/table/CustomTable";
import SearchHeaderContainer from "../../components/table/SearchHeaderContainer";
import PageHeader from "../../components/PageHeader";
import Actions from "../../components/Actions";
import CoachFormDialog from "../../components/classes/CoachFormDialog";
import CoachPayoutDialog from "../../components/classes/CoachPayoutDialog";
import { Coach } from "../../components/classes/types";
import {
  COMPENSATION_OPTIONS,
  formatMoney,
  isCoachActive,
  isCustomPerClassCompensation,
  labelForValue,
  toActiveLabel,
} from "../../components/classes/classUtils";
import { getAllCoaches } from "../../services/CoachesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { IAction } from "../../types/table/interfaces.type";

type CoachRow = {
  id: string;
  full_name: string;
  phone: string;
  country: string;
  compensation_type: string;
  compensation_rate: string;
  active: string;
  total_payout: number;
  raw: Coach;
};

const compensationOptions = COMPENSATION_OPTIONS.map((o) => ({
  label: o.label,
  value: String(o.value),
}));

const Coaches: React.FC = () => {
  const [rows, setRows] = React.useState<CoachRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [refetchTrigger, setRefetchTrigger] = React.useState(0);
  const [formOpen, setFormOpen] = React.useState(false);
  const [editingCoach, setEditingCoach] = React.useState<Coach | null>(null);
  const [payoutCoach, setPayoutCoach] = React.useState<Coach | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    const fetchCoaches = async () => {
      setLoading(true);
      try {
        const coaches = await getAllCoaches();
        const mapped: CoachRow[] = coaches.map((coach: any) => ({
          id: String(coach.id),
          full_name: coach.full_name || coach.name || "-",
          phone: coach.phone || "-",
          country: coach.country || "-",
          compensation_type: labelForValue(
            compensationOptions,
            coach.coach_profile?.compensation_type
          ),
          compensation_rate: isCustomPerClassCompensation(
            coach.coach_profile?.compensation_type
          )
            ? "Manual per session"
            : formatMoney(coach.coach_profile?.compensation_rate),
          active: toActiveLabel(isCoachActive(coach)),
          total_payout: Number(coach.total_payout) || 0,
          raw: coach,
        }));
        if (!cancelled) setRows(mapped);
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to fetch coaches."));
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCoaches();
    return () => {
      cancelled = true;
    };
  }, [refetchTrigger]);

  const findCoach = (id: string | number) =>
    rows.find((row) => String(row.id) === String(id))?.raw || null;

  const payoutAction: IAction<any> = {
    id: "payout",
    label: "Payout",
    name: "Payout",
    icon: (row: CoachRow) => (
      <Tooltip title="Payout report">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <PaidIcon sx={{ color: "#2e7d32", width: 23 }} />
          {row.total_payout > 0 && (
            <Box component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: "#2e7d32" }}>
              {formatMoney(row.total_payout)}
            </Box>
          )}
        </Box>
      </Tooltip>
    ),
    handler: ({ id }: any) => setPayoutCoach(findCoach(id)),
  };

  const actions = [
    ...Actions({
      handleOpenEditModal: {
        handler: (id) => {
          setEditingCoach(findCoach(id));
          setFormOpen(true);
        },
      },
    }),
    payoutAction,
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <Box sx={{ mt: { xs: 2, sm: 3 } }}>
        <PageHeader
          title="Coaches"
          subtitle="The coaches who run your classes."
        />
        <SearchHeaderContainer
          search={search}
          setSearch={setSearch}
          buttonConfig={[
            {
              size: "small",
              bgColor: COLORS.MainColor,
              startIcon: <AddIcon />,
              label: "Add Coach",
              onClick: () => {
                setEditingCoach(null);
                setFormOpen(true);
              },
              textColor: COLORS.WhiteText,
              minHeight: 36,
              maxHeight: 36,
              sx: {
                width: { xs: "96%", sm: "auto" },
                marginRight: { xs: 1.5, sm: 0 },
              },
            },
          ]}
        />
        <CustomTable<CoachRow>
          filters={{ page: 0, size: 10, search }}
          clientSidePagination
          loading={loading}
          fields={[
            { key: "full_name", header: "Coach" },
            { key: "phone", header: "Phone" },
            { key: "country", header: "Country" },
            { key: "compensation_type", header: "Compensation" },
            { key: "compensation_rate", header: "Rate" },
            { key: "active", header: "Status" },
          ]}
          actions={actions}
          fetchFunc={async (params?: any) => {
            const query = String((params && params.search) || search || "")
              .toLowerCase()
              .trim();

            const content = rows.filter((row) => {
              if (!query) return true;
              return [
                row.full_name,
                row.phone,
                row.country,
                row.compensation_type,
                row.active,
              ]
                .filter(Boolean)
                .map((v) => String(v).toLowerCase())
                .join(" ")
                .includes(query);
            });

            const pageSize = (params && params.size) || 10;
            return {
              content,
              number: 0,
              size: pageSize,
              totalElements: content.length,
              totalPages: Math.max(1, Math.ceil(content.length / pageSize)),
            };
          }}
        />

        <CoachFormDialog
          open={formOpen}
          coach={editingCoach}
          onClose={() => {
            setFormOpen(false);
            setEditingCoach(null);
          }}
          onSaved={() => setRefetchTrigger((prev) => prev + 1)}
        />

        <CoachPayoutDialog
          open={Boolean(payoutCoach)}
          coach={payoutCoach}
          onClose={() => setPayoutCoach(null)}
          onPaid={() => setRefetchTrigger((prev) => prev + 1)}
        />
      </Box>
    </Box>
  );
};

export default Coaches;
